// Salary Service - CRUD for teamgehalt table (separate from team data for security)
// Only GL members or the person themselves should call these functions.
// Access control is enforced at the component level.

const SALARY_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "teamgehalt",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${SALARY_CONFIG.storageAccount}.table.core.windows.net/${SALARY_CONFIG.tableName}`;

const generateSalaryId = () =>
  `sal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Ensure the teamgehalt table exists
const ensureTableExists = async () => {
  try {
    const url = `https://${SALARY_CONFIG.storageAccount}.table.core.windows.net/Tables?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: SALARY_CONFIG.tableName }),
    });
    // 201 = Created, 409 = Already exists
    return response.ok || response.status === 409;
  } catch {
    return false;
  }
};

// Load all salary entries for a specific team member
export const getSalaryEntries = async (teamMemberId) => {
  try {
    const baseUrl = getBaseUrl();
    const filter = `&$filter=PartitionKey eq 'salary' and TeamMemberId eq '${teamMemberId}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}()?${SALARY_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .map((item) => ({
        id: item.RowKey,
        teamMemberId: item.TeamMemberId || "",
        brutto: parseFloat(item.Brutto) || 0,
        netto: parseFloat(item.Netto) || 0,
        pensum: item.Pensum !== undefined ? parseFloat(item.Pensum) : null,
        abDatum: item.AbDatum || "",
        dreizehnter: item.Dreizehnter === true,
        createdAt: item.CreatedAt || "",
        updatedAt: item.UpdatedAt || "",
      }))
      .sort((a, b) => (a.abDatum > b.abDatum ? -1 : 1)); // Newest first
  } catch (error) {
    console.error("Fehler beim Laden der Gehaltsdaten:", error);
    return [];
  }
};

// Create a new salary entry
export const createSalaryEntry = async (entryData) => {
  try {
    await ensureTableExists();

    const rowKey = generateSalaryId();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "salary",
      RowKey: rowKey,
      TeamMemberId: entryData.teamMemberId,
      Brutto: parseFloat(entryData.brutto) || 0,
      Netto: parseFloat(entryData.netto) || 0,
      Pensum: entryData.pensum != null ? parseFloat(entryData.pensum) : 100,
      AbDatum: entryData.abDatum || "",
      Dreizehnter: entryData.dreizehnter === true,
      CreatedAt: now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Create salary entry failed: ${response.status} - ${errorText}`);
    }

    return {
      id: rowKey,
      teamMemberId: entryData.teamMemberId,
      brutto: parseFloat(entryData.brutto) || 0,
      netto: parseFloat(entryData.netto) || 0,
      pensum: entryData.pensum != null ? parseFloat(entryData.pensum) : 100,
      abDatum: entryData.abDatum || "",
      dreizehnter: entryData.dreizehnter === true,
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Fehler beim Erstellen des Gehaltseintrags:", error);
    throw error;
  }
};

// Update an existing salary entry
export const updateSalaryEntry = async (entryId, entryData) => {
  try {
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "salary",
      RowKey: entryId,
      TeamMemberId: entryData.teamMemberId,
      Brutto: parseFloat(entryData.brutto) || 0,
      Netto: parseFloat(entryData.netto) || 0,
      Pensum: entryData.pensum != null ? parseFloat(entryData.pensum) : 100,
      AbDatum: entryData.abDatum || "",
      Dreizehnter: entryData.dreizehnter === true,
      CreatedAt: entryData.createdAt || now,
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}(PartitionKey='salary',RowKey='${entryId}')?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Update salary entry failed: ${response.status} - ${errorText}`);
    }

    return {
      id: entryId,
      ...entryData,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Gehaltseintrags:", error);
    throw error;
  }
};

// Delete a single salary entry
export const deleteSalaryEntry = async (entryId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='salary',RowKey='${entryId}')?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete salary entry failed: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen des Gehaltseintrags:", error);
    throw error;
  }
};

// Delete ALL salary entries for a team member (used when deleting the person)
// Also removes the manual PK contribution entity (PartitionKey 'pkmanuell').
export const deleteAllSalaryEntries = async (teamMemberId) => {
  try {
    const entries = await getSalaryEntries(teamMemberId);
    await Promise.all(entries.map((entry) => deleteSalaryEntry(entry.id)));
    await deletePkManualEntry(teamMemberId).catch(() => {});
    return entries.length;
  } catch (error) {
    console.error("Fehler beim Löschen aller Gehaltseinträge:", error);
    throw error;
  }
};

// ========== MANUELLE PK-BEITRAEGE (PartitionKey 'pkmanuell') ==========
// Used when the active PK insurance rate has berechnungsModus === "manuell":
// GL enters MA (AN) and AG contribution per person; alongside the values a
// snapshot of the context (year, pensum, active salary entry) is stored so
// stale values can be detected (Pensumänderung, Neueintritt, Jahreswechsel,
// Lohnänderung). One entity per person, RowKey = teamMemberId (employeeUuid).

const mapPkManualEntity = (item) => ({
  teamMemberId: item.RowKey,
  anBeitrag: parseFloat(item.AnBeitrag) || 0,
  agBeitrag: parseFloat(item.AgBeitrag) || 0,
  standJahr: parseInt(item.StandJahr) || 0,
  standPensum: item.StandPensum !== undefined ? parseFloat(item.StandPensum) : null,
  standSalaryId: item.StandSalaryId || "",
  updatedAt: item.UpdatedAt || "",
});

// Load the manual PK entry for one person (or null)
export const getPkManualEntry = async (teamMemberId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='pkmanuell',RowKey='${teamMemberId}')?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);
    return mapPkManualEntity(await response.json());
  } catch {
    return null;
  }
};

// Load ALL manual PK entries (GL only — access control at component level)
export const getAllPkManualEntries = async () => {
  try {
    const baseUrl = getBaseUrl();
    const filter = `&$filter=PartitionKey eq 'pkmanuell'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${baseUrl}()?${SALARY_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries.map(mapPkManualEntity);
  } catch {
    return [];
  }
};

// Upsert the manual PK entry for one person (PUT = insert or replace)
export const savePkManualEntry = async (teamMemberId, data) => {
  try {
    await ensureTableExists();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: "pkmanuell",
      RowKey: teamMemberId,
      AnBeitrag: parseFloat(data.anBeitrag) || 0,
      AgBeitrag: parseFloat(data.agBeitrag) || 0,
      StandJahr: parseInt(data.standJahr) || new Date().getFullYear(),
      StandPensum: data.standPensum != null ? parseFloat(data.standPensum) : 100,
      StandSalaryId: data.standSalaryId || "",
      UpdatedAt: now,
    };

    const url = `${getBaseUrl()}(PartitionKey='pkmanuell',RowKey='${teamMemberId}')?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Save PK manual entry failed: ${response.status} - ${errorText}`);
    }

    return mapPkManualEntity(entity);
  } catch (error) {
    throw error;
  }
};

// Delete the manual PK entry for one person
export const deletePkManualEntry = async (teamMemberId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='pkmanuell',RowKey='${teamMemberId}')?${SALARY_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Delete PK manual entry failed: ${response.status}`);
    }
    return true;
  } catch (error) {
    throw error;
  }
};

// Get the currently active salary for a team member (latest abDatum <= today)
export const getCurrentSalary = async (teamMemberId) => {
  try {
    const entries = await getSalaryEntries(teamMemberId);
    const today = new Date().toISOString().split("T")[0];

    const currentEntry = entries.find((entry) => entry.abDatum <= today);
    return currentEntry || null;
  } catch (error) {
    console.error("Fehler beim Laden des aktuellen Gehalts:", error);
    return null;
  }
};
